import React from 'react';
import {
    Card,
    Typography,
    Space,
    Button,
    Table,
    message,
    Tooltip,
    Alert,
    Popconfirm,
    Input
} from 'antd';
import {
    SyncOutlined,
    PlayCircleOutlined,
    FileTextOutlined,
    WarningOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../api/axios';

const { Title, Text, Paragraph } = Typography;

interface WebPLogDto {
    timestamp: string;
    mediaFileId: string;
    fileName: string;
    originalPath: string;
    webpPath: string;
    cmsUpdatesCount: number;
    oleUpdatesCount: number;
    details: string[];
}

export const MediaJobs: React.FC = () => {
    const queryClient = useQueryClient();
    const [searchText, setSearchText] = React.useState('');

    // Query to fetch WebP conversion logs
    const { data: logs = [], isLoading, refetch } = useQuery<WebPLogDto[]>({
        queryKey: ['adminWebPLogs'],
        queryFn: async () => {
            const response = await apiClient.get<WebPLogDto[]>('/admin/media/webp-job/logs');
            return response.data;
        }
    });

    // Mutation to trigger WebP conversion job
    const triggerJobMutation = useMutation({
        mutationFn: async () => {
            const response = await apiClient.post('/admin/media/webp-job/trigger');
            return response.data;
        },
        onSuccess: (data: any) => {
            message.success(data?.message || 'WebP conversion job completed successfully!');
            queryClient.invalidateQueries({ queryKey: ['adminWebPLogs'] });
        },
        onError: (error: any) => {
            console.error('WebP conversion job failed:', error);
            message.error(error?.response?.data?.detail || 'Failed to trigger WebP conversion job.');
        }
    });

    const reconvertAllMutation = useMutation({
        mutationFn: async () => {
            const response = await apiClient.post('/admin/media/webp-job/reconvert-all');
            return response.data;
        },
        onSuccess: (data: any) => {
            message.success(data?.message || 'Tüm görseller yeniden dönüştürülmek üzere işaretlendi ve görev başlatıldı.');
            queryClient.invalidateQueries({ queryKey: ['adminWebPLogs'] });
        },
        onError: (error: any) => {
            console.error('Re-convert all job failed:', error);
            message.error(error?.response?.data?.detail || 'Tümünü yeniden dönüştürme işlemi başlatılamadı.');
        }
    });

    const handleTriggerJob = () => {
        triggerJobMutation.mutate();
    };

    const filteredLogs = logs.filter(log => 
        log.fileName.toLowerCase().includes(searchText.toLowerCase()) || 
        log.originalPath.toLowerCase().includes(searchText.toLowerCase()) || 
        log.webpPath.toLowerCase().includes(searchText.toLowerCase())
    );

    const columns = [
        {
            title: 'Tarih',
            dataIndex: 'timestamp',
            key: 'timestamp',
            render: (text: string) => new Date(text).toLocaleString('tr-TR'),
            sorter: (a: WebPLogDto, b: WebPLogDto) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
            defaultSortOrder: 'descend' as const,
        },
        {
            title: 'Dosya Adı',
            dataIndex: 'fileName',
            key: 'fileName',
            sorter: (a: WebPLogDto, b: WebPLogDto) => a.fileName.localeCompare(b.fileName),
            render: (text: string) => <Text code>{text}</Text>,
        },
        {
            title: 'Orijinal Yol',
            dataIndex: 'originalPath',
            key: 'originalPath',
            ellipsis: true,
            sorter: (a: WebPLogDto, b: WebPLogDto) => a.originalPath.localeCompare(b.originalPath),
            render: (text: string) => (
                <Tooltip title={text}>
                    <Text type="secondary">{text}</Text>
                </Tooltip>
            ),
        },
        {
            title: 'WebP Yol',
            dataIndex: 'webpPath',
            key: 'webpPath',
            ellipsis: true,
            sorter: (a: WebPLogDto, b: WebPLogDto) => a.webpPath.localeCompare(b.webpPath),
            render: (text: string) => (
                <Tooltip title={text}>
                    <Text type="success">{text}</Text>
                </Tooltip>
            ),
        },
        {
            title: 'CMS Güncelleme',
            dataIndex: 'cmsUpdatesCount',
            key: 'cmsUpdatesCount',
            align: 'center' as const,
            sorter: (a: WebPLogDto, b: WebPLogDto) => a.cmsUpdatesCount - b.cmsUpdatesCount,
            render: (count: number) => (
                <span style={{ fontWeight: count > 0 ? 'bold' : 'normal' }}>
                    {count}
                </span>
            ),
        },
        {
            title: 'OLE Güncelleme',
            dataIndex: 'oleUpdatesCount',
            key: 'oleUpdatesCount',
            align: 'center' as const,
            sorter: (a: WebPLogDto, b: WebPLogDto) => a.oleUpdatesCount - b.oleUpdatesCount,
            render: (count: number) => (
                <span style={{ fontWeight: count > 0 ? 'bold' : 'normal' }}>
                    {count}
                </span>
            ),
        }
    ];

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <Title level={2}>WebP Görsel Dönüştürücü</Title>
                    <Text type="secondary">Sitedeki görsellerin otomatik WebP dönüşümlerini ve içerik entegrasyonlarını yönetin.</Text>
                </div>
                <Space>
                    <Input.Search 
                        placeholder="Görsel veya yol ara..." 
                        allowClear 
                        onSearch={(val) => setSearchText(val)}
                        onChange={(e) => setSearchText(e.target.value)}
                        style={{ width: 250 }} 
                    />
                    <Button 
                        icon={<SyncOutlined spin={isLoading} />} 
                        onClick={() => refetch()}
                    >
                        Yenile
                    </Button>
                </Space>
            </div>

            <Card>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Title level={4}>Dönüştürme ve Güncelleme İşlemi</Title>
                    <Paragraph>
                        Bu işlem, sistemde kayıtlı olan ancak henüz WebP formatı oluşturulmamış tüm görselleri (PNG, JPG, JPEG, JFIF vb.) 
                        yüksek performanslı ve sıkıştırılmış WebP formatına çevirir. Dönüşümün ardından, site içeriklerindeki (Sayfa içerikleri, 
                        Slider görsel yolları, Kurslar, Projeler vb.) eski görsel adresleri otomatik olarak yeni WebP yollarıyla güncellenir.
                    </Paragraph>
                    <Alert
                        message="Not: Büyük resim dosyalarının işlenmesi ve veri tabanı güncellemeleri, kütüphanedeki resim sayısına bağlı olarak birkaç saniye sürebilir."
                        type="info"
                        showIcon
                    />
                    <div style={{ marginTop: 10 }}>
                        <Space>
                            <Button
                                type="primary"
                                size="large"
                                icon={triggerJobMutation.isPending ? <SyncOutlined spin /> : <PlayCircleOutlined />}
                                onClick={handleTriggerJob}
                                loading={triggerJobMutation.isPending || reconvertAllMutation.isPending}
                                style={{ height: '48px', borderRadius: '6px' }}
                            >
                                {triggerJobMutation.isPending ? 'WebP Dönüşümü Çalıştırılıyor...' : 'Görselleri Dönüştür ve İçeriği Güncelle'}
                            </Button>

                            <Popconfirm
                                title="Tüm görseller yeniden dönüştürülecek!"
                                description="Bu işlem, kalite ayarına göre eski resimler de dahil tüm görselleri sıfırdan oluşturacaktır. Devam etmek istiyor musunuz?"
                                onConfirm={() => reconvertAllMutation.mutate()}
                                okText="Evet, Dönüştür"
                                cancelText="İptal"
                                okButtonProps={{ danger: true }}
                            >
                                <Button
                                    danger
                                    type="default"
                                    size="large"
                                    icon={reconvertAllMutation.isPending ? <SyncOutlined spin /> : <WarningOutlined />}
                                    loading={reconvertAllMutation.isPending || triggerJobMutation.isPending}
                                    style={{ height: '48px', borderRadius: '6px' }}
                                >
                                    Tümünü Yeniden Dönüştür
                                </Button>
                            </Popconfirm>
                        </Space>
                    </div>
                </Space>
            </Card>

            <Card title={<span><FileTextOutlined /> Dönüşüm ve Değişiklik Günlükleri</span>}>
                <Table
                    dataSource={filteredLogs}
                    columns={columns}
                    rowKey={(record) => record.mediaFileId + record.timestamp}
                    loading={isLoading}
                    pagination={{ pageSize: 15 }}
                    locale={{ emptyText: 'Henüz WebP dönüşüm kaydı bulunmamaktadır.' }}
                    expandable={{
                        expandedRowRender: (record) => (
                            <div style={{ margin: 0, paddingLeft: 24 }}>
                                <Text strong>Güncellenen İçerikler:</Text>
                                {record.details && record.details.length > 0 ? (
                                    <ul style={{ marginTop: 8 }}>
                                        {record.details.map((detail, index) => (
                                            <li key={index}><Text type="secondary">{detail}</Text></li>
                                        ))}
                                    </ul>
                                ) : (
                                    <Paragraph type="secondary" style={{ marginTop: 8, fontStyle: 'italic' }}>
                                        Herhangi bir içerik güncellemesi kaydedilmemiş.
                                    </Paragraph>
                                )}
                            </div>
                        ),
                        rowExpandable: (record) => record.cmsUpdatesCount > 0 || record.oleUpdatesCount > 0,
                    }}
                />
            </Card>
        </Space>
    );
};
